import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import NodeBadge, { NodeBadgeProps, BadgeVariant } from './NodeBadge';
import Tooltip from '../../../common/Tooltip';

export interface BadgeConfig {
  /** Unique identifier for the badge */
  id: string;
  /** Icon component to display */
  icon?: React.ReactNode;
  /** Badge label text */
  label: string;
  /** Badge styling variant */
  variant?: BadgeVariant;
  /** Custom color scheme */
  colorScheme?: string;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Tooltip text */
  tooltip?: string;
  /** Priority for sorting (lower = higher priority, shown first) */
  priority: number;
}

export interface BadgeContainerProps {
  /** Array of badge configurations */
  badges: BadgeConfig[];
  /** Maximum number of badges to show before collapsing (default: 2) */
  maxVisible?: number;
  /** Layout direction */
  layout?: 'horizontal' | 'vertical';
  /** Gap between badges */
  gap?: 'tight' | 'normal' | 'loose';
  /** Additional CSS classes */
  className?: string;
}

const gapClasses = {
  tight: 'gap-1',
  normal: 'gap-2',
  loose: 'gap-3',
};

/**
 * Container for node badges with overflow handling
 * Shows first N badges inline, remaining badges in a "+X" overflow indicator
 * Hover popover shows all badges
 */
const BadgeContainer: React.FC<BadgeContainerProps> = ({
  badges,
  maxVisible = 2,
  layout = 'horizontal',
  gap = 'normal',
  className = '',
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sort badges by priority (lower number = higher priority)
  const sortedBadges = [...badges].sort((a, b) => a.priority - b.priority);
  const visibleBadges = sortedBadges.slice(0, maxVisible);
  const overflowBadges = sortedBadges.slice(maxVisible);
  const hasOverflow = overflowBadges.length > 0;

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (overflowRef.current) {
      const rect = overflowRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2,
      });
    }
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    // Small delay to allow moving to popover
    timeoutRef.current = setTimeout(() => {
      setShowPopover(false);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    setShowPopover(false);
  };

  const layoutClasses = layout === 'horizontal'
    ? `flex flex-wrap items-center justify-center ${gapClasses[gap]}`
    : `flex flex-col items-center ${gapClasses[gap]}`;

  return (
    <div
      ref={containerRef}
      className={`${layoutClasses} ${className}`}
    >
      {/* Visible badges */}
      {visibleBadges.map((badge) => (
        <NodeBadge
          key={badge.id}
          icon={badge.icon}
          label={badge.label}
          variant={badge.variant}
          colorScheme={badge.colorScheme}
          onClick={badge.onClick}
          tooltip={badge.tooltip}
        />
      ))}

      {/* Overflow indicator */}
      {hasOverflow && (
        <>
          <Tooltip content={`${overflowBadges.length} more badge${overflowBadges.length > 1 ? 's' : ''}`}>
            <button
              ref={overflowRef}
              type="button"
              className="flex items-center justify-center px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => {
                e.stopPropagation();
                setShowPopover(!showPopover);
              }}
            >
              +{overflowBadges.length}
            </button>
          </Tooltip>

          {/* Popover portal */}
          {showPopover && ReactDOM.createPortal(
            <div
              ref={popoverRef}
              className="fixed bg-white dark:bg-cap-dark rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-[100] min-w-[150px]"
              style={{
                top: popoverPosition.top,
                left: popoverPosition.left,
                transform: 'translateX(-50%)',
              }}
              onMouseEnter={handlePopoverMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
            >
              {/* Arrow */}
              <div
                className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-white dark:border-b-cap-dark"
                style={{ filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))' }}
              />

              {/* All overflow badges */}
              <div className="flex flex-col gap-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 font-medium px-1">
                  Additional Features
                </div>
                {overflowBadges.map((badge) => (
                  <NodeBadge
                    key={badge.id}
                    icon={badge.icon}
                    label={badge.label}
                    variant={badge.variant}
                    colorScheme={badge.colorScheme}
                    onClick={badge.onClick}
                    tooltip={badge.tooltip}
                  />
                ))}
              </div>
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
};

export default BadgeContainer;
